import { z } from 'zod'
import { ContainerType, NoteType, TaskStatus } from './m1.ts'

/**
 * MCP (§13, ADR 0009): consent + "Verbundene KI-Clients" (REST below /api/v1/mcp) and the tool schemas of
 * services/mcp. Tool parameters are snake_case and described in English (better tool choice by models,
 * §13.3); contents stay German.
 */

const id = z.uuid()
const isoDate = z.iso.date()
const isoDateTime = z.iso.datetime({ offset: true })

// ── Grants (REST) ─────────────────────────────────────────────────────────────────────────────
/** all = every container · default = all except local_only/no_ai · selected = the chosen ones (+ their sub-containers) */
export const McpContainerMode = z.enum(['all', 'default', 'selected'])
export type McpContainerMode = z.infer<typeof McpContainerMode>

const GrantFields = {
  containerMode: McpContainerMode,
  containerIds: z.array(id).max(500),
  canWrite: z.boolean(),
}

export const McpGrant = z.object({
  clientId: z.string(),
  clientName: z.string(),
  ...GrantFields,
  lastUsedAt: isoDateTime.nullable(),
  createdAt: isoDateTime,
})
export type McpGrant = z.infer<typeof McpGrant>

// GET /mcp/grants → { grants: McpGrant[] } · PATCH /mcp/grants/:clientId (UpdateMcpGrant) · DELETE /mcp/grants/:clientId
export const UpdateMcpGrant = z.object(GrantFields).partial()
export type UpdateMcpGrant = z.infer<typeof UpdateMcpGrant>

/** GET /mcp/consent?oauth_query=… – what the consent page shows (the signed query is verified server-side) */
export const McpConsentInfo = z.object({
  clientId: z.string(),
  clientName: z.string(),
  clientUri: z.string().nullable(),
  icon: z.string().nullable(),
  /** requested OAuth scopes, e.g. ["brain:read","brain:write","offline_access"] */
  scopes: z.array(z.string()),
  /** the current grant if this client was connected before (pre-fills the form) */
  grant: McpGrant.nullable(),
})
export type McpConsentInfo = z.infer<typeof McpConsentInfo>

/** POST /mcp/consent → { url } (redirect target: the client's redirect_uri with code, or an error) */
export const McpConsent = z.object({
  oauthQuery: z.string().min(1).max(8000),
  accept: z.boolean(),
  containerMode: McpContainerMode.default('default'),
  containerIds: z.array(id).max(500).default([]),
  canWrite: z.boolean().default(false),
})
export type McpConsent = z.infer<typeof McpConsent>

// ── Tool inputs ───────────────────────────────────────────────────────────────────────────────
const limit = (max: number, def: number) => z.number().int().min(1).max(max).default(def)
const ObjectType = z.enum([
  'note',
  'task',
  'meeting',
  'decision',
  'person',
  'container',
  'commitment',
  'file',
  'source',
])

export const McpTools = {
  search: z.object({
    query: z
      .string()
      .trim()
      .min(1)
      .max(500)
      .describe('Search text (German or English). Supports "exact phrase" and -exclude.'),
    containers: z.array(id).max(50).optional().describe('Only search these container ids'),
    para: ContainerType.optional().describe('Only containers of this PARA type'),
    types: z.array(NoteType).max(10).optional().describe('Only notes of these types, e.g. ["meeting"]'),
    from: isoDate.optional().describe('Updated on or after (YYYY-MM-DD)'),
    to: isoDate.optional().describe('Updated before (YYYY-MM-DD)'),
    include_archived: z.boolean().default(false),
    limit: limit(20, 10),
  }),
  fetch: z.object({
    id: id.describe('Id from search or another tool'),
    type: ObjectType.optional().describe('Object type if known (faster); otherwise detected'),
    include_transcript: z.boolean().default(false).describe('Meetings only: include the full transcript'),
  }),
  list_containers: z.object({
    para: ContainerType.optional(),
    status: z.enum(['active', 'on_hold', 'done', 'archived', 'all']).default('active'),
  }),
  get_project_brief: z.object({ container_id: id }),
  list_tasks: z.object({
    container_id: id.optional(),
    status: z.union([TaskStatus, z.literal('open')]).default('open'),
    due_before: isoDate.optional().describe('Due on or before (YYYY-MM-DD)'),
    priority: z.number().int().min(1).max(4).optional().describe('1 = highest'),
    agent_ready: z.boolean().optional(),
    limit: limit(100, 25),
    cursor: z.string().max(20).optional().describe('From next_cursor of the previous page'),
  }),
  get_today: z.object({ date: isoDate.optional().describe('Default: today (Europe/Berlin)') }),
  plan_day: z.object({
    date: isoDate.optional().describe('Default: today (Europe/Berlin)'),
  }),
  accept_day_plan: z.object({
    date: isoDate.optional().describe('Default: today (Europe/Berlin)'),
    confirm: z
      .literal(true)
      .describe(
        'Only true after the user explicitly confirmed writing the plan to their calendar in this chat',
      ),
  }),
  prep_meeting: z.object({
    meeting_id: id.optional().describe('A meeting in the brain'),
    query: z.string().trim().max(200).optional().describe('Title or participant name if no id is known'),
  }),
  weekly_review: z.object({ container_id: id.optional() }),
  capture: z.object({
    text: z.string().trim().min(1).max(20_000),
    container_hint: z.string().max(200).optional().describe('Container id or slug; otherwise the inbox'),
    type_hint: z.enum(['note', 'task']).default('note'),
  }),
  create_note: z.object({
    title: z.string().trim().min(1).max(300),
    markdown: z.string().max(200_000).default(''),
    container_id: id.nullable().default(null).describe('null = inbox'),
    parent_note_id: id.optional().describe('Create as sub-page of this note'),
    type: NoteType.default('note'),
  }),
  append_to_note: z.object({
    note_id: id,
    markdown: z.string().min(1).max(200_000),
  }),
  create_task: z.object({
    title: z.string().trim().min(1).max(500),
    container_id: id.nullable().default(null).describe('null = inbox'),
    description: z.string().max(50_000).optional(),
    priority: z.number().int().min(1).max(4).optional().describe('1 = highest, default 3'),
    due_date: isoDate.optional(),
    do_date: isoDate.optional(),
    estimate_min: z.number().int().min(0).max(10_000).optional(),
    agent_ready: z.boolean().optional(),
    spec: z.string().max(50_000).optional().describe('Specification for an agent'),
  }),
  update_task: z.object({
    task_id: id,
    title: z.string().trim().min(1).max(500).optional(),
    status: TaskStatus.optional(),
    description: z.string().max(50_000).optional(),
    priority: z.number().int().min(1).max(4).optional(),
    due_date: isoDate.nullable().optional(),
    do_date: isoDate.nullable().optional(),
    estimate_min: z.number().int().min(0).max(10_000).nullable().optional(),
    agent_ready: z.boolean().optional(),
    spec: z.string().max(50_000).nullable().optional(),
    container_id: id.nullable().optional(),
  }),
  complete_task: z.object({ task_id: id }),
  log_decision: z.object({
    title: z.string().trim().min(1).max(500),
    rationale: z.string().max(50_000).default(''),
    container_id: id.nullable().default(null),
    supersedes_id: id.optional().describe('Decision this one replaces (marked superseded and linked)'),
    decided_at: isoDateTime.optional(),
  }),
  add_commitment: z.object({
    text: z.string().trim().min(1).max(2000),
    direction: z
      .enum(['i_owe', 'owed_to_me'])
      .describe('i_owe = I promised something; owed_to_me = someone promised me'),
    person_id: id.optional(),
    container_id: id.nullable().default(null),
    due_date: isoDate.optional(),
  }),
} as const
export type McpToolName = keyof typeof McpTools

/** Tools that change data – only offered when the grant allows writing. */
export const MCP_WRITE_TOOLS: readonly McpToolName[] = [
  'capture',
  'create_note',
  'append_to_note',
  'create_task',
  'update_task',
  'complete_task',
  'plan_day',
  'accept_day_plan',
  'log_decision',
  'add_commitment',
]

/** Every tool result carries the object id and two links (§13.2). */
export const McpRef = z.object({
  id: z.string(),
  type: ObjectType,
  title: z.string(),
  url: z.string().describe('Web URL'),
  deep_link: z.string().describe('brain:// link for the Mac app'),
})
export type McpRef = z.infer<typeof McpRef>
